import React, { useState, useEffect, useContext } from 'react'
import MapContext from '../Map/MapContext';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import { TileWMS, Vector as VectorSource } from 'ol/source'
import DragAndDrop from 'ol/interaction/DragAndDrop'
import GeoJSON from 'ol/format/GeoJSON'
import KML from 'ol/format/KML'
import GPX from 'ol/format/GPX'
import IGC from 'ol/format/IGC'
import TopoJSON from 'ol/format/TopoJSON'
import Tool from '../Tool/Tool';
import { useTranslation } from 'react-i18next';
import DragAndDropOption from './DragAndDropOption';
import Accordion from '../Bootstrap/Accordion/Accordion';

import './LoadAdditionalLayers.css'

// TODO: dar formato, crear boton para carga de wms

/**
 * CARGA DE CAPA WMS
 * Datos mínimos necesarios: URL y nombre capa
 * Implementar funcionalidad adicional para seleccionar una capa específica del servicio WMS
 * realizando una solicitud GetCapabilities para obtener información sobre las capas disponibles
 * @returns
 */
const LoadAdditionalLayers = ({
  title,
  toggleable,
  type,
  hasSidebar,
  buttonName,
  buttonText
}) => {
  const { map } = useContext(MapContext)
  const [wmsUrl, setWMSUrl] = useState('')
  const [selectedLayer, setSelectedLayer] = useState('')
  const [availableLayers, setAvailableLayers] = useState([])
  const [loadedLayers, setLoadedLayers] = useState([])
  const { t } = useTranslation();
  // const { setNewLayerExterna } = useGlobalVariablesContext()

  // Añadir soporte Drag & Drop para ficheros locales (GeoJSON, KML, GPX, IGC, TopoJSON)
  useEffect(() => {
    if (!map) return

    const dragAndDropInteraction = new DragAndDrop({
      formatConstructors: [GeoJSON, KML, GPX, IGC, TopoJSON]
    })

    const handleAddFeatures = (event) => {
      if (!event.features || event.features.length === 0) return

      const vectorSource = new VectorSource({
        features: event.features
      })

      const fileName = event.file && event.file.name ? event.file.name : `Capa ${Date.now()}`

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        visible: true,
      })

      vectorLayer.set('externa', true)
      vectorLayer.set('queryable', false)
      vectorLayer.set('title', fileName)

      // Asignar un zIndex alto para que las capas dinámicas aparezcan por encima de las capas base
      vectorLayer.setZIndex(100)

      map.addLayer(vectorLayer)

      setLoadedLayers(prev => [
        ...prev,
        {
          id: Date.now(),
          name: fileName,
          layer: vectorLayer,
          visible: true,
          url: null
        }
      ])
    }

    dragAndDropInteraction.on('addfeatures', handleAddFeatures)
    map.addInteraction(dragAndDropInteraction)

    return () => {
      try {
        dragAndDropInteraction.un('addfeatures', handleAddFeatures)
        map.removeInteraction(dragAndDropInteraction)
      } catch (e) {
        // noop
      }
    }
  }, [map])

  const addLayer = () => {
    if (!map || !wmsUrl || !selectedLayer) return

    const wmsSource = new TileWMS({
      url: wmsUrl,
      params: {
        LAYERS: selectedLayer,
        TILED: true
      },
      serverType: 'geoserver'
    })

    const wmsLayer = new TileLayer({
      queryable: false,
      externa: true,
      source: wmsSource,
      title: selectedLayer,
      visible: true,
      opacity: 0.7
    })

    // Asignar un zIndex alto para que las capas WMS dinámicas aparezcan por encima de las capas base
    wmsLayer.setZIndex(100)

    map.addLayer(wmsLayer)

    setLoadedLayers(prev => [...prev, {
      id: Date.now(),
      name: selectedLayer,
      layer: wmsLayer,
      visible: true,
      url: wmsUrl
    }])

    // Solo limpiar la selección de capa, mantener la URL
    setSelectedLayer('')
  }

  const toggleLayerVisibility = (layerId) => {
    setLoadedLayers(prev => prev.map(layer => {
      if (layer.id === layerId) {
        layer.layer.setVisible(!layer.visible)
        return { ...layer, visible: !layer.visible }
      }
      return layer
    }))
  }

  const removeLayer = (layerId) => {
    const layerToRemove = loadedLayers.find(layer => layer.id === layerId)
    if (layerToRemove) {
      map.removeLayer(layerToRemove.layer)
      setLoadedLayers(prev => prev.filter(layer => layer.id !== layerId))
    }
  }

  const clearAllLayers = () => {
    loadedLayers.forEach(layer => {
      map.removeLayer(layer.layer)
    })
    setLoadedLayers([])
  }

  // Manejar cambios en la entrada de texto para la URL del servicio WMS
  const handleInputChange = (event) => {
    setWMSUrl(event.target.value)
  }

  // Manejar cambios en la selección de capa
  const handleLayerChange = (event) => {
    setSelectedLayer(event.target.value)
  }

  // Función para obtener las capas disponibles a través de una solicitud GetCapabilities
  const fetchAvailableLayers = async () => {
    try {
      // Realizar una solicitud GetCapabilities al servicio WMS para obtener información sobre las capas disponibles
      const response = await fetch(
        `${wmsUrl}?request=GetCapabilities&service=WMS`
      )

      if (!response.ok) {
        throw new Error(
          `Error al obtener las capas disponibles: ${response.status}`
        )
      }

      const text = await response.text()

      // Procesar la respuesta para extraer los nombres de las capas disponibles
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(text, 'text/xml')

      // Busca y extrae el contenido de texto de todos los elementos <Name> que se encuentran dentro de
      // elementos <Layer> en el documento XML
      const layers = Array.from(xmlDoc.querySelectorAll('Layer Name')).map(
        (node) => node.textContent
      )
      // Elimina los duplicados de la lista de capas
      const layersUnicos = [...new Set(layers)];

      // Actualizar availableLayers con los nombres de las capas disponibles
      setAvailableLayers(layersUnicos)
    } catch (error) {
      console.error('Error al obtener las capas disponibles:', error)
    }
  }

  // Efecto secundario para obtener las capas disponibles cuando se cambia la URL del servicio WMS
  useEffect(() => {
    if (wmsUrl) {
      fetchAvailableLayers() // Obtener las capas disponibles
    }
  }, [wmsUrl])

  const handleToolClose = () => {
    // Solo cerramos el sidebar pero mantenemos el estado de catastro
    setToolState('aditional_WMS', { sidebarOpen: false });
  };

  // Componente para la sección WMS
  const WMSContent = () => (
    <div className='content-load wms'>
      <input
        type="text"
        id="wmsUrl"
        placeholder="URL del servicio WMS"
        value={wmsUrl}
        onChange={handleInputChange}
        className="form-control mb-2 w-100"
      />

      <select
        value={selectedLayer}
        onChange={handleLayerChange}
        className="form-select mb-2 w-100"
      >
        <option value=''>Selecciona una capa</option>
        {availableLayers.map((layer) => (
          <option key={layer} value={layer}>{layer}</option>
        ))}
      </select>

      <button
        onClick={addLayer}
        className="btn btn-primary p-2 mb-4"
        title="Añadir Capa WMS al Mapa"
        style={{
          width: '100%'
        }}
        disabled={!wmsUrl || !selectedLayer}
      >
        <i className="bi bi-plus-circle me-2"></i>
        Añadir Capa WMS al Mapa
      </button>

      <br />
      <span className="small text-muted mb-3 ">* Ingrese la URL del servicio WMS, seleccione la capa a añadir y pulse "Añadir capa WMS al mapa".<br/> https://wms.mapama.gob.es/sig/Biodiversidad/RedNatura/wms.aspx</span>
    </div>
  );

  // Componente para la sección de ficheros externos
  const ExternalFilesContent = () => (
    <div className='content-load-layers'>
      <DragAndDropOption />
    </div>
  );

  // Componente para la gestión de capas cargadas
  const LoadedLayersContent = () => (
    loadedLayers.length > 0 && (
      <>
        {/* listado de capas añadidas */}
        <div className="d-flex flex-column gap-2 w-100">
          {loadedLayers.map(layer => (
            <div
              key={layer.id}
              className="d-flex align-items-center justify-content-between p-2 bg-light rounded w-100"
            >
              <span className="text-truncate me-3 flex-grow-1">{layer.name}</span>
              <div className="d-flex gap-2 flex-shrink-0">
                <button
                  onClick={() => toggleLayerVisibility(layer.id)}
                  className="btn btn-light btn-sm"
                  title={layer.visible ? 'Ocultar capa' : 'Mostrar capa'}
                >
                  <i className={`bi ${layer.visible ? 'bi-eye-fill' : 'bi-eye-slash-fill'}`}></i>
                </button>
                <button
                  onClick={() => removeLayer(layer.id)}
                  className="btn btn-danger btn-sm"
                  title="Eliminar capa"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          title="Limpiar todas las capas"
          style={{
            width: '100%'
          }}
          onClick={clearAllLayers}
          className="btn btn-secondary p-2 mt-4"
        >
          <i className="bi bi-trash me-2"></i>
          Limpiar todas las capas
        </button>
      </>
    )
  );

  const sidebarContent = (
    <div className='content-load-layers'>
      <Accordion 
        title={t('Cargar servicios WMS')} 
        icon="bi bi-globe2"
        defaultOpen={true}
      >
        <WMSContent />
      </Accordion>

      <Accordion 
        title={t('Cargar ficheros externos')} 
        icon="bi-file-earmark-arrow-up"
        defaultOpen={false}
      >
        <ExternalFilesContent />
      </Accordion>

      {loadedLayers.length > 0 && (
        <Accordion 
          title={t('Capas cargadas')} 
          icon="bi-layers"
          defaultOpen={true}
        >
          <LoadedLayersContent />
        </Accordion>
      )}
    </div>
  );

  return (
    <Tool
      id="aditional_WMS"
      type="toggleable"
      icon='bi bi-plus-square-dotted'
      label={t('Cargar capas adicionales')}
      sidebarTitle={t('Cargar capas adicionales')}
      hasSidebar
      sidebarContent={sidebarContent}
      onClose={handleToolClose}
    />
  )
}

export default LoadAdditionalLayers
